import * as React from 'react';
import { Send, Sparkles, Loader2, Mic, Paperclip } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';

export interface SuggestionChip {
  id: string;
  label: string;
  prompt: string;
  icon?: React.ReactNode;
}

export interface PromptBarProps {
  onSubmit: (prompt: string) => void;
  suggestions?: SuggestionChip[];
  placeholder?: string;
  isProcessing?: boolean;
  disabled?: boolean;
  showVoiceButton?: boolean;
  showAttachButton?: boolean;
  className?: string;
}

export const PromptBar = React.forwardRef<HTMLDivElement, PromptBarProps>(
  (
    {
      onSubmit,
      suggestions = [],
      placeholder = 'Ask anything about your business...',
      isProcessing = false,
      disabled = false,
      showVoiceButton = false,
      showAttachButton = false,
      className,
    },
    ref,
  ) => {
    const [prompt, setPrompt] = React.useState('');
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    // Auto-resize textarea based on content
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setPrompt(value);

      // Auto-resize textarea
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        const newHeight = Math.min(
          Math.max(textareaRef.current.scrollHeight, 40),
          96,
        );
        textareaRef.current.style.height = `${newHeight}px`;
      }
    };

    // Handle keyboard submission
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    };

    // Handle form submission
    const handleSubmit = () => {
      const trimmedPrompt = prompt.trim();
      if (trimmedPrompt && !isProcessing) {
        onSubmit(trimmedPrompt);
        setPrompt('');

        // Reset textarea height
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
      }
    };

    // Handle suggestion chip click
    const handleSuggestionClick = (suggestion: SuggestionChip) => {
      setPrompt(suggestion.prompt);

      // Focus textarea and set height
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.style.height = 'auto';
        setTimeout(() => {
          if (textareaRef.current) {
            const newHeight = Math.min(
              Math.max(textareaRef.current.scrollHeight, 40),
              96,
            );
            textareaRef.current.style.height = `${newHeight}px`;
          }
        }, 0);
      }
    };

    return (
      <div
        ref={ref}
        className={cn(
          'flex w-full flex-col gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4 shadow-sm transition-colors focus-within:border-[var(--color-primary)] focus-within:shadow-md',
          disabled && 'opacity-50',
          className,
        )}
      >
        {/* Suggestion chips */}
        {suggestions && suggestions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.id}
                onClick={() => handleSuggestionClick(suggestion)}
                disabled={isProcessing || disabled}
                className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-secondary)]/50 px-3 py-1.5 text-xs font-medium text-[var(--color-secondary-foreground)] transition-all hover:bg-[var(--color-secondary)] hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                {suggestion.icon && (
                  <span className="flex items-center justify-center">
                    {suggestion.icon}
                  </span>
                )}
                <span>{suggestion.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Input area with buttons */}
        <div className="flex gap-2">
          {/* Textarea */}
          <div className="flex flex-1 flex-col">
            <Textarea
              ref={textareaRef}
              value={prompt}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={isProcessing || disabled}
              className={cn(
                'min-h-[40px] max-h-[96px] resize-none rounded-md border border-[var(--color-input)] bg-[var(--color-background)] px-3 py-2 text-sm placeholder:text-[var(--color-muted-foreground)] focus-visible:border-[var(--color-primary)] focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/20 disabled:cursor-not-allowed disabled:opacity-50',
              )}
              rows={1}
            />
          </div>

          {/* Right-side action buttons */}
          <div className="flex flex-col items-center justify-end gap-2">
            {/* Submit button */}
            <Button
              onClick={handleSubmit}
              disabled={!prompt.trim() || isProcessing || disabled}
              variant="default"
              size="icon"
              className={cn(
                'h-10 w-10 flex-shrink-0 transition-all',
                isProcessing && 'bg-[var(--color-primary)] opacity-75',
              )}
              title="Send (Enter)"
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>

            {/* Optional buttons */}
            {showAttachButton && (
              <Button
                variant="ghost"
                size="icon"
                disabled={isProcessing || disabled}
                className="h-10 w-10 flex-shrink-0 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                title="Attach file"
              >
                <Paperclip className="h-4 w-4" />
              </Button>
            )}

            {showVoiceButton && (
              <Button
                variant="ghost"
                size="icon"
                disabled={isProcessing || disabled}
                className="h-10 w-10 flex-shrink-0 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                title="Voice input"
              >
                <Mic className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Helper text */}
        {isProcessing && (
          <div className="flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Processing your request...</span>
          </div>
        )}

        {!isProcessing && prompt.length === 0 && (
          <div className="text-xs text-[var(--color-muted-foreground)]">
            Tip: Use Shift+Enter for multiple lines
          </div>
        )}
      </div>
    );
  },
);

PromptBar.displayName = 'PromptBar';
